import React from 'react';
import TableHeader from 'components/table/TableHeader';
import TableRow from 'components/table/TableRow';
import TableCell from 'components/table/TableCell';
import Table from 'components/table/Table';
import BlueButton from 'components/BlueButton';
import PlusIcon from 'assets/images/icons/PlusIcon';
import StatusBadge from 'components/StatusBadge';
import DotsMenu from 'components/DotsMenu';
import {
  setActiveTab,
  setIsEditing,
  setClient,
} from 'features/client/clientSlice';
import {useSelector} from 'react-redux';
import {AppDispatch, RootState} from '../../store/store';
import {useDispatch} from 'react-redux';
import {useDropdownActionMenu} from '../../hooks/useDropdownActionMenu';
import {useNavigate} from 'react-router-dom';
import {SearchType} from '../../types/search';
import {ClientThunks} from './clientThunks';
import {Client} from '../../types/client';
import TrashIcon from '../../assets/images/icons/TrashIcon';
import usePermissions from '../../hooks/usePermissions';

const ClientsTable: React.FC<{openModal: () => void}> = ({openModal}) => {
  const clients = useSelector((state: RootState) => state.client.clients);
  const pagy = useSelector((state: RootState) => state.client.pagy || {});
  const loading = useSelector(
    (state: RootState) => state.client.status === 'loading',
  );
  const activeTab = useSelector(
    (state: RootState) => state.client.activeTab || 'All',
  );
  const dispatch = useDispatch<AppDispatch>();
  const {setDotsMenuIsOpen} = useDropdownActionMenu();
  const navigate = useNavigate();
  const {checkPermission} = usePermissions();

  interface VisebleColumn {
    caseId: boolean;
    firstName: boolean;
    lastName: boolean;
    program: boolean;
    assignedTo: boolean;
    status: boolean;
    referralSource: boolean;
    payer: boolean;
    location: boolean;
  }
  const [visibleColumns, setVisibleColumns] = React.useState<VisebleColumn>({
    caseId: true,
    firstName: true,
    lastName: true,
    program: true,
    assignedTo: true,
    status: true,
    referralSource: true,
    payer: true,
    location: true,
  });

  const columnOptions = [
    {label: 'Case ID', value: 'caseId'},
    {label: 'First Name', value: 'firstName'},
    {label: 'Last Name', value: 'lastName'},
    {label: 'Program', value: 'program'},
    {label: 'Assigned To', value: 'assignedTo'},
    {label: 'Status', value: 'status'},
    {label: 'Referral Source', value: 'referralSource'},
    {label: 'Payer', value: 'payer'},
    {label: 'Location', value: 'location'},
  ];

  const searchByOptions = [
    {label: 'Case ID', value: 'id', operator: '=='},
    {label: 'First Name', value: 'first_name', operator: 'like'},
    {label: 'Last Name', value: 'last_name', operator: 'like'},
  ];

  const statusTabs = ['All', 'Intake', 'Assessment', 'Active', 'Discharged'];

  const fetchData = async ({
    page,
    search,
    sortField,
    sortDirection,
    rowsPerPage,
  }: {
    page: number;
    search: SearchType<{[key: string]: {operator: string; value: string}}>;
    sortField: string;
    sortDirection: string;
    rowsPerPage: number;
  }): Promise<void> => {
    const validStatuses = ['Intake', 'Assessment', 'Active', 'Discharged'];
    if (validStatuses.includes(activeTab)) {
      search.where.status = {
        operator: '==',
        value: activeTab.toLowerCase(),
      };
    } else {
      search.where.status = {
        operator: '==',
        value: '',
      };
    }
    await dispatch(
      ClientThunks.index({
        page,
        search,
        sortField,
        sortDirection,
        rowsPerPage,
      }),
    );
  };

  const handleColumnToggle = (column: string) => {
    if (column in visibleColumns) {
      setVisibleColumns(prevState => ({
        ...prevState,
        [column]: !prevState[column as keyof VisebleColumn],
      }));
    }
  };

  const handleOpenClient = (id?: string) => {
    navigate(`/clients/${id}`);
  };

  const handleOpenModal = () => {
    dispatch(setClient(null));
    dispatch(setIsEditing(true));
    openModal();
  };

  const handleDeleteClient = (clientId: string) => {
    dispatch(ClientThunks.delete(clientId));
    setDotsMenuIsOpen(null);
  };

  const handleChangeStatus = (clientId: string, status: string) => {
    dispatch(ClientThunks.update({id: clientId, status: status}));
    setDotsMenuIsOpen(null);
  };
  const handleEditClient = async (clientId: string) => {
    try {
      // Fetch client data
      await dispatch(ClientThunks.show(clientId));
      dispatch(setIsEditing(true));
      openModal();
    } catch (error) {
      console.error('Error loading client:', error);
    }
    setDotsMenuIsOpen(null);
  };

  return (
    <Table
      fetchData={fetchData}
      pagy={pagy}
      pageable={true}
      activeTab={activeTab}
      setActiveTab={(tab: string) => dispatch(setActiveTab(tab))}
      tabs={statusTabs}
      onColumnToggle={handleColumnToggle}
      searchByOptions={searchByOptions}
      recordsName={'Clients'}
      columnOptions={columnOptions}
      searchable={true}
      loading={loading}
      columns={true}
      blueButton={
        <BlueButton onClick={handleOpenModal} label="" icon={<PlusIcon />} />
      }>
      <thead>
        <tr>
          {visibleColumns.caseId && <TableHeader label="Case ID" />}
          {visibleColumns.firstName && <TableHeader label="First Name" />}
          {visibleColumns.lastName && <TableHeader label="Last Name" />}
          {visibleColumns.program && <TableHeader label="Program" />}
          {visibleColumns.assignedTo && <TableHeader label="Assigned To" />}
          {visibleColumns.status && <TableHeader label="Status" />}
          {visibleColumns.referralSource && (
            <TableHeader label="Referral Source" />
          )}
          {visibleColumns.payer && <TableHeader label="Payer" />}
          {visibleColumns.location && <TableHeader label="Location" />}
          <TableHeader label="" />
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200 dark:divide-neutral-700">
        {clients.map((client: Client) => {
          const userOptions = [];
          
          // Edit button - always available if user has update permission
          if (checkPermission('clients', 'update')) {
            userOptions.push({
              label: 'Edit',
              onClick: () => handleEditClient(client.id),
              hoverClass: 'hover:bg-cyan-100 ',
            });
          }
          
          if (checkPermission('clients', 'update')) {
            if (client.status !== 'Discharged') {
              userOptions.push({
                label: 'Move to Discharged',
                onClick: () => handleChangeStatus(client.id, 'discharged'),
                hoverClass: 'hover:bg-cyan-100 ',
              });
            }
            if (client.status !== 'intake') {
              userOptions.push({
                label: 'Move to Intake',
                onClick: () => handleChangeStatus(client.id, 'intake'),
                hoverClass: 'hover:bg-cyan-100 ',
              });
            }
            userOptions.push({
              label: 'Delete',
              onClick: () => handleDeleteClient(client.id),
              icon: <TrashIcon />,
              hoverClass: 'hover:bg-red-100 hover:text-red-800',
            });
          }

          return (
            <TableRow key={client.id}>
              {visibleColumns.caseId && (
                <TableCell>
                  <div className=" relative group">
                    <div className="w-full flex items-center gap-x-3">
                      <div className="flex-grow">
                        {checkPermission('update', 'Program') ? (
                          <span
                            className="text-sm cursor-pointer font-medium dark:text-neutral-200 underline text-cyan-600"
                            onClick={() => handleOpenClient(client.id)}>
                            {client.id}
                          </span>
                        ) : (
                          <span className="text-sm font-medium text-gray-700 dark:text-neutral-300">
                            {client.caseId}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </TableCell>
              )}
              {visibleColumns.firstName && (
                <TableCell>{client.firstName}</TableCell>
              )}
              {visibleColumns.lastName && (
                <TableCell>{client.lastName}</TableCell>
              )}
              {visibleColumns.program && (
                <TableCell>
                  {typeof client.program === 'string' 
                    ? client.program 
                    : client.program?.name}
                </TableCell>
              )}
              {visibleColumns.assignedTo && (
                <TableCell>{client.assignedTo}</TableCell>
              )}
              {visibleColumns.status && (
                <TableCell>
                  <StatusBadge status={client.status} />
                </TableCell>
              )}
              {visibleColumns.referralSource && (
                <TableCell>
                  {typeof client.referralSource === 'string' 
                    ? client.referralSource 
                    : client.referralSource?.name}
                </TableCell>
              )}
              {visibleColumns.payer && (
                <TableCell>
                  {client.payers?.map(payer => payer.name).join(', ') || '-'}
                </TableCell>
              )}
              {visibleColumns.location && (
                <TableCell>
                  {typeof client.location === 'string' 
                    ? client.location 
                    : client.location?.name}
                </TableCell>
              )}
              <TableCell>
                <DotsMenu options={userOptions} />
              </TableCell>
            </TableRow>
          );
        })}
      </tbody>
    </Table>
  );
};

export default ClientsTable;
